import React, { useState, useEffect } from 'react';
import { Heart, EyeClosed, Gem, ArrowRight, Sparkles, Menu } from 'lucide-react';
import NavBar from '../components/Navbar/NavBar';
import { motion, AnimatePresence } from 'framer-motion';

const AroundWorld = () => {
  const [hoveredService, setHoveredService] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.pageYOffset;
      setIsVisible(currentScrollPos > 100);
      
      // Update active section based on scroll position
      const sections = ['hero', 'about', 'team', 'services', 'blog'];
      const sectionElements = sections.map(id => document.getElementById(id));
      const currentSection = sectionElements.findIndex(el => {
        if (!el) return false;
        const rect = el.getBoundingClientRect();
        return rect.top <= window.innerHeight / 2 && rect.bottom >= window.innerHeight / 2;
      });
      if (currentSection !== -1) {
        setActiveSection(sections[currentSection]);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const teamMembers = [
    {
      name: "Kammoun Khouloud",
      role: "Event Designer",
      description: "With over 15 years of experience in curating luxury weddings, Khouloud transforms dreams into reality with elegance and precision.",
      expertise: ["Exclusive Event Curation", "Luxury Vendor Networking", "Timeless Wedding Aesthetics"],
      social: { linkedin: "#", twitter: "#" },
      experience: "15+ years crafting unforgettable luxury weddings."
    },
    {
      name: "Khawla Kammoun",
      role: "Creative Director & Design Specialist",
      description: "A master of sophisticated wedding aesthetics, Khawla brings visionary artistry to every event, ensuring elegance in every detail.",
      expertise: ["Bespoke Wedding Designs", "Floral Artistry", "Trendsetting Wedding Styles"],
      social: { linkedin: "#", twitter: "#" },
      experience: "10+ years designing high-end wedding experiences."
    }
  ];

  const premiumServices = [
    {
      icon: EyeClosed,
      title: "Event Design & Scenography",
      description: "Transforming spaces with bespoke concepts and immersive storytelling, specializing in custom set design and bespoke décor."
    },
    {
      icon: Heart,
      title: "Luxury Wedding Planning",
      description: "Crafting elegant, tailor-made weddings in stunning locations worldwide, combining creative flair with flawless execution."
    },
    {
      icon: Gem,
      title: "Corporate Events & Brand Experiences",
      description: "Creating impactful product launches, gala dinners, conferences, and memorable brand activations."
    }
  ];

  const blogPosts = [
    {
      title: "Top 10 Wedding Planning Tips for Stress-Free Luxury Weddings",
      content: "Planning a luxury wedding can be overwhelming without the right approach. From choosing the perfect venue to coordinating with premium vendors, every step matters. Discover our top 10 expert tips to ensure your luxury wedding is elegant, seamless, and unforgettable.",
      link: "#"
    },
    {
      title: "Destination Wedding Guide: How to Plan a Dream Wedding Abroad",
      content: "Destination weddings are magical but come with unique challenges. Learn how to select the right destination, manage guest logistics, and work with local suppliers for a truly spectacular event.",
      link: "#"
    },
    {
      title: "Wedding Decor Trends 2025: Elevate Your Big Day",
      content: "Stay ahead of the trends with our exclusive insight into 2025's wedding decor ideas. From floral artistry to immersive lighting, these design tips will elevate your celebration.",
      link: "#"
    }
  ];

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const scaleIn = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1 }
  };

  const slideIn = {
    hidden: { opacity: 0, x: -50 },
    visible: { opacity: 1, x: 0 }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black text-white overflow-hidden">
      <NavBar />

      {/* Mobile Navigation Menu */}
      <div className="lg:hidden fixed top-4 right-4 z-50">
        <motion.button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 rounded-full bg-white/10 backdrop-blur-xl"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <Menu className="w-6 h-6" />
        </motion.button>
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="absolute right-0 mt-2 w-48 rounded-xl bg-white/10 backdrop-blur-xl p-4"
            >
              {['hero', 'about', 'team', 'services', 'blog'].map((section) => (
                <motion.button
                  key={section}
                  onClick={() => {
                    document.getElementById(section)?.scrollIntoView({ behavior: 'smooth' });
                    setIsMobileMenuOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-sm rounded-lg hover:bg-white/20 transition-colors"
                >
                  {section.charAt(0).toUpperCase() + section.slice(1)}
                </motion.button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Desktop Navigation Dots */}
      <div className="hidden lg:flex fixed right-8 top-1/2 transform -translate-y-1/2 z-50 flex-col gap-4">
        {['hero', 'about', 'team', 'services', 'blog'].map((section) => (
          <motion.button
            key={section}
            onClick={() => {
              document.getElementById(section)?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="relative w-3 h-3 rounded-full bg-white/20 backdrop-blur-sm"
            whileHover={{ scale: 1.2 }}
            whileTap={{ scale: 0.9 }}
          >
            <motion.div
              className="absolute inset-0 rounded-full bg-white"
              initial={false}
              animate={{ scale: activeSection === section ? 1 : 0 }}
              transition={{ duration: 0.2 }}
            />
          </motion.button>
        ))}
      </div>

      {/* Hero Section with Enhanced Parallax */}
      <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden px-4 sm:px-6">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-black opacity-60"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-purple-900/30 to-blue-900/30"></div>
          <motion.div
            className="absolute inset-0"
            initial={{ scale: 1.2 }}
            animate={{ scale: 1 }}
            transition={{ duration: 2, ease: "easeOut" }}
          >
            <div className="absolute inset-0 bg-[url('https://res.cloudinary.com/dx5y2bzdq/image/upload/v1741611868/WhatsApp_Image_2025-03-07_%C3%A0_17.58.22_a33cedcc_tq6hkm.jpg')] bg-cover bg-center opacity-20"></div>
          </motion.div>
        </div>
        <motion.div 
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          transition={{ duration: 0.8 }}
          className="container mx-auto text-center relative z-10"
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mb-4 sm:mb-8"
          >
            <Sparkles className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-purple-400" />
          </motion.div>
          <h1 className="text-4xl sm:text-6xl md:text-8xl font-extrabold leading-tight mb-6 sm:mb-12 tracking-wide bg-clip-text text-transparent bg-gradient-to-r from-white via-purple-400 to-blue-400">
            Crafting Unforgettable Experiences
          </h1>
          <motion.p 
            className="text-lg sm:text-xl md:text-2xl max-w-3xl mx-auto mb-8 sm:mb-16 text-gray-300 leading-relaxed px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            2K Events Group curates exceptional events where design, technology, and artistry come together to create breathtaking and unforgettable experiences.
          </motion.p>
          <motion.a 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            href="#services" 
            className="inline-flex items-center px-6 sm:px-12 py-3 sm:py-4 text-base sm:text-lg font-medium text-white border-2 border-white rounded-full hover:bg-white hover:text-black transition-all duration-300"
          >
            <span className="relative z-10">Discover Our Expertise</span>
            <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-purple-500 to-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              initial={false}
              animate={{ scale: 1.2 }}
              transition={{ duration: 0.3 }}
            />
          </motion.a>
        </motion.div>
      </section>

      {/* About Section with Enhanced Glass Effect */}
      <section id="about" className="py-16 sm:py-32 relative px-4 sm:px-6">
        <div className="container mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="backdrop-blur-xl bg-white/5 rounded-3xl p-8 sm:p-16 text-center border border-white/10 shadow-2xl"
          >
            <motion.h2 
              className="text-4xl sm:text-6xl font-bold mb-8 sm:mb-16 bg-gradient-to-r from-white via-purple-400 to-blue-400 bg-clip-text text-transparent"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              About Us
            </motion.h2>
            <motion.p 
              className="text-base sm:text-xl max-w-4xl mx-auto mb-8 leading-relaxed text-gray-300"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              2K Events Group is a premier wedding and event planning agency in Dubai and Tunis, specializing in luxury weddings and exclusive events.
              With over 10 years of combined experience, we transform visions into spectacular reality with creativity and precision.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Team Section with Enhanced Cards */}
      <section id="team" className="py-16 sm:py-32 px-4 sm:px-6">
        <div className="container mx-auto">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl sm:text-6xl font-bold mb-12 sm:mb-20 text-center bg-gradient-to-r from-white via-purple-400 to-blue-400 bg-clip-text text-transparent"
          >
            Meet Our Experts
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-16">
            {teamMembers.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="group relative overflow-hidden rounded-2xl"
              >
                <div className="backdrop-blur-xl bg-white/5 p-6 sm:p-10 transition-all duration-500 group-hover:bg-white/10 border border-white/10">
                  <motion.div
                    initial={{ scale: 1 }}
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h3 className="text-2xl sm:text-3xl font-bold mb-3 bg-gradient-to-r from-white to-purple-400 bg-clip-text text-transparent">{member.name}</h3>
                    <p className="text-lg sm:text-xl text-purple-300 mb-4 sm:mb-6">{member.role}</p>
                    <p className="text-sm sm:text-base text-gray-300 mb-4 sm:mb-6">{member.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {member.expertise.map((skill, i) => (
                        <motion.span
                          key={i}
                          initial={{ opacity: 0, scale: 0.8 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ delay: i * 0.1 }}
                          className="inline-block px-3 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm bg-white/10 backdrop-blur-sm border border-white/10"
                        >
                          {skill}
                        </motion.span>
                      ))}
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section with Enhanced Interactive Cards */}
      <section id="services" className="py-16 sm:py-32 relative px-4 sm:px-6">
        <div className="container mx-auto">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl sm:text-6xl font-bold mb-12 sm:mb-20 text-center bg-gradient-to-r from-white via-purple-400 to-blue-400 bg-clip-text text-transparent"
          >
            Exclusive Services
          </motion.h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {premiumServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="group relative overflow-hidden rounded-2xl"
              >
                <div 
                  className={`backdrop-blur-xl bg-white/5 p-6 sm:p-10 h-full transition-all duration-500
                    ${hoveredService === index ? 'bg-white/10' : ''} border border-white/10`}
                  onMouseEnter={() => setHoveredService(index)}
                  onMouseLeave={() => setHoveredService(null)}
                >
                  <motion.div
                    initial={{ scale: 1 }}
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.3 }}
                    className="mb-6 sm:mb-8"
                  >
                    <service.icon className="w-12 h-12 sm:w-16 sm:h-16 text-purple-400" strokeWidth={1.5} />
                  </motion.div>
                  <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 bg-gradient-to-r from-white to-purple-400 bg-clip-text text-transparent">{service.title}</h3>
                  <p className="text-sm sm:text-base text-gray-300">{service.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Section with Enhanced Cards */}
      <section id="blog" className="py-16 sm:py-32 px-4 sm:px-6">
        <div className="container mx-auto">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl sm:text-6xl font-bold mb-12 sm:mb-20 text-center bg-gradient-to-r from-white via-purple-400 to-blue-400 bg-clip-text text-transparent"
          >
            Wedding Planning Tips & Insights
          </motion.h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {blogPosts.map((post, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="group cursor-pointer"
              >
                <div className="backdrop-blur-xl bg-white/5 rounded-2xl p-6 sm:p-8 h-full transition-all duration-500 hover:bg-white/10 border border-white/10">
                  <motion.h3 
                    className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 group-hover:text-purple-400 transition-colors"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 }}
                  >
                    {post.title}
                  </motion.h3>
                  <motion.p 
                    className="text-sm sm:text-base text-gray-300 mb-4 sm:mb-6"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.4 }}
                  >
                    {post.content}
                  </motion.p>
                  <motion.div 
                    className="flex items-center text-purple-400 group-hover:translate-x-2 transition-transform"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5 }}
                  >
                    <span className="mr-2 text-sm sm:text-base">Read More</span>
                    <ArrowRight className="w-4 h-4" />
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced Scroll to Top Button */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: isVisible ? 1 : 0 }}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-4 sm:bottom-8 right-4 sm:right-8 p-3 sm:p-4 rounded-full bg-white/10 backdrop-blur-xl hover:bg-white/20 transition-all duration-300 border border-white/10 shadow-lg"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 rotate-[-90deg]" />
      </motion.button>
    </div>
  );
};

export default AroundWorld;
